import React, { useEffect, useMemo, useState, useRef } from 'react';
import DataTable from 'react-data-table-component';
import { CSVLink } from 'react-csv';
import { Edit, Trash } from 'lucide-react';
import Drawer from '../Drawer';
import AddPartner from './AddPartner';
import PartnerEdit from './PartnerEdit';
import FiltersPanel from '../FiltersPanel';
import CustomAlert from '../CustomAlert';
import { t, useLanguage } from '../../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

export default function PartnerList() {
  const { lang } = useLanguage();
  const [partners, setPartners] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'success' });
  const dropdownRef = useRef(null);

  const fetchPartners = () => {
    setLoading(true);
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/partners`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then((res) => res.json())
      .then((data) => setPartners(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Failed to fetch partners:', err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  const handleEditClick = (id) => {
    setEditId(id);
  };

  // Deleting confirms in place — the list stays put, so the row simply
  // disappears once the server has accepted it.
  const handleDeleteClick = (partner) => {
    setDeleteTarget(partner);
  };

  const confirmDelete = () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    const token = localStorage.getItem('token');

    fetch(`${apiUrl}/partners/${deleteTarget.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || t('Партнерот не е избришан'));
        setDeleteTarget(null);
        setAlert({ show: true, type: 'success', message: data.message || t('Партнерот е избришан.') });
        fetchPartners();
      })
      .catch((err) => {
        setDeleteTarget(null);
        setAlert({ show: true, type: 'error', message: err.message || t('Партнерот не е избришан') });
      })
      .finally(() => setDeleting(false));
  };

  const allColumns = useMemo(() => [
    { id: 'ID', name: 'ID', selector: (row) => row.id, sortable: true, width: '70px' },
    {
      id: 'Назив', name: t('Назив'),
      selector: (row) => row.name,
      sortable: true,
      grow: 2,
      cell: (row) => <span className="font-semibold text-gray-900">{row.name}</span>,
    },
    { id: 'Локација', name: t('Локација'), selector: (row) => row.address || '-', sortable: true, grow: 2 },
    { id: 'Телефон', name: t('Телефон'), selector: (row) => row.phone || '-', sortable: true, width: '140px' },
    { id: 'Е-пошта', name: t('Е-пошта'), selector: (row) => row.email || '-', sortable: true },
    { id: 'Матичен број', name: t('Матичен број'), selector: (row) => row.bussiness_id || '-', sortable: true },
    { id: 'Даночен број', name: t('Даночен број'), selector: (row) => row.tax_id || '-', sortable: true },
    {
      id: 'Веб-страница', name: t('Веб-страница'),
      selector: (row) => row.website || '-',
      sortable: true,
      cell: (row) => row.website ? (
        <a href={row.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
          {row.website}
        </a>
      ) : '-',
    },
    { id: 'Банка', name: t('Банка'), selector: (row) => row.bank || '-', sortable: true },
    { id: 'Банкарска сметка', name: t('Банкарска сметка'), selector: (row) => row.bank_number || '-', sortable: true },
    { id: 'Овластено лице', name: t('Овластено лице'), selector: (row) => row.primary_person || '-', sortable: true },
    { id: 'Дополнителен контакт', name: t('Дополнителен контакт'), selector: (row) => row.secondary_person || '-', sortable: true },
    {
      id: 'Креирана на', name: t('Креирана на'),
      selector: (row) => (row.created_at ? new Date(row.created_at).toLocaleDateString('mk-MK') : ''),
      sortable: true,
      width: '140px',
    },
    {
      id: 'Акции', name: t('Акции'),
      cell: (row) => (
        <div className="flex gap-2 items-center">
          <button
            onClick={() => handleEditClick(row.id)}
            title={t('Уреди')}
            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors"
          >
            <Edit size={18} />
          </button>
          <button
            onClick={() => handleDeleteClick(row)}
            title={t('Избриши')}
            className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
          >
            <Trash size={18} />
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '120px',
    },
  ], [lang]);

  const [visibleColumns, setVisibleColumns] = useState(
    allColumns
      .map((col) => col.id)
      .filter((name) =>
        ['ID', 'Назив', 'Локација', 'Телефон', 'Е-пошта', 'Овластено лице', 'Акции'].includes(name)
      )
  );

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) =>
      prev.includes(columnName) ? prev.filter((name) => name !== columnName) : [...prev, columnName]
    );
  };

  const columns = useMemo(() => allColumns.filter((col) => visibleColumns.includes(col.id)), [allColumns, visibleColumns]);

  const filteredPartners = useMemo(() => {
    return partners.filter(
      (p) =>
        p.name?.toLowerCase().includes(filter.toLowerCase()) ||
        p.address?.toLowerCase().includes(filter.toLowerCase()) ||
        p.phone?.toLowerCase().includes(filter.toLowerCase()) ||
        p.email?.toLowerCase().includes(filter.toLowerCase())
    );
  }, [partners, filter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownRef]);

  if (loading) {
    return (
      <div className="h-full bg-gray-50 flex justify-center items-center">
        <div className="text-center">
          <svg className="animate-spin h-12 w-12 mx-auto text-blue-500 mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600 text-lg">{t('Вчитување на партнери...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
        {/* Filters Section */}
        <FiltersPanel>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2 mb-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('Пребарај')}</label>
              <input
                type="text"
                placeholder={t('Назив, адреса, телефон или е-пошта...')}
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="w-full px-3 py-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 justify-start sm:justify-end">
            <button
              onClick={() => setFilter('')}
              className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
              title={t('Избриши го филтерот')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span>{t('Избриши')}</span>
            </button>
          </div>
                </FiltersPanel>

        {/* Action Bar */}
        <div className="flex flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 justify-start sm:justify-end">
          <div className="flex gap-2 items-center flex-wrap justify-start sm:justify-end w-full sm:w-auto">
            <button
              onClick={() => setAddOpen(true)}
              className="w-full sm:w-auto justify-center px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium text-sm transition-colors flex items-center space-x-2"
              title={t('Нов партнер')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>{t('Нов Партнер')}</span>
            </button>
          </div>
        </div>

        {/* Partners Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden max-w-full">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h2 className="text-sm font-semibold text-gray-900">{t('Партнери')}</h2>
            <div className="flex gap-2 items-center">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium text-sm transition-colors flex items-center space-x-1.5"
                  title={t('Избери колони')}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 4v16M15 4v16" />
                  </svg>
                  <span className="hidden sm:inline">{t('Колони')}</span>
                </button>

                {showDropdown && (
                  <div className="absolute right-0 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-50 mt-2">
                    <div className="p-3">
                      <p className="text-xs font-semibold text-gray-600 mb-2 uppercase">{t('Прикажи колони')}</p>
                      <div className="space-y-2 max-h-64 overflow-auto">
                        {allColumns.map((col) => (
                          <label key={col.id} className="flex items-center cursor-pointer hover:bg-blue-50 p-2 rounded transition-colors">
                            <input
                              type="checkbox"
                              checked={visibleColumns.includes(col.id)}
                              onChange={() => toggleColumn(col.id)}
                              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-0"
                            />
                            <span className="ml-3 text-sm text-gray-700">{col.name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white rounded-md font-medium text-sm transition-colors flex items-center space-x-1.5"
                onClick={fetchPartners}
                disabled={loading}
                title={t('Освежи ја листата')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">{loading ? t('Вчитување...') : t('Освежи')}</span>
              </button>

              <CSVLink
                data={filteredPartners}
                filename="partners.csv"
                target="_blank"
                className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium text-sm transition-colors no-underline flex items-center space-x-1.5"
                title={t('Експортирај ги партнерите во CSV')}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                <span className="hidden sm:inline">{t('Експорт')}</span>
              </CSVLink>
            </div>
          </div>
          <DataTable
            columns={columns}
            data={filteredPartners}
            pagination
            highlightOnHover={false}
            striped={false}
            responsive
            persistTableHead
            noDataComponent={
              <div className="p-12 text-center">
                <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m5-2.13a4 4 0 100-8 4 4 0 000 8zm6 0a4 4 0 100-8" />
                </svg>
                <p className="text-gray-600">{t('Нема пронајдени партнери')}</p>
              </div>
            }
          />
        </div>
      </div>

      {/* Wide panel: the form lays out in 3 columns to fill it. */}
      <Drawer open={addOpen} onClose={() => setAddOpen(false)} title={t('Нов партнер')} width="max-w-4xl">
        <AddPartner
          onSuccess={() => {
            setAddOpen(false);
            fetchPartners();
          }}
        />
      </Drawer>

      <Drawer open={editId !== null} onClose={() => setEditId(null)} title={t('Измени партнер')} width="max-w-4xl">
        {/* key remounts the form when another row is picked, so it refetches. */}
        <PartnerEdit
          key={editId}
          id={editId}
          onSuccess={() => {
            setEditId(null);
            fetchPartners();
          }}
        />
      </Drawer>

      {/* Delete confirmation — replaces the old /partners/delete/:id page. */}
      <CustomAlert
        isOpen={deleteTarget !== null}
        type="error"
        title={t('Избриши партнер')}
        message={t('Дали сте сигурни дека сакате да го избришете „{name}“?', { name: deleteTarget?.name ?? '' })}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        showConfirmButton={true}
        confirmText={deleting ? t('Се брише...') : t('Избриши')}
        cancelText={t('Откажи')}
      />

      <CustomAlert
        isOpen={alert.show}
        type={alert.type}
        title={alert.type === 'success' ? t('Успешно') : t('Грешка')}
        message={alert.message}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
      />
    </div>
  );
}
